import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PhoneInTalkOutlined,
  RequestQuoteOutlined,
} from '@mui/icons-material';
import { salesPortalService } from '../../services/salesPortalService';
import { salesActivityLogService } from '../../services/salesActivityLogService';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  ACTIVITY_TYPE_META,
  SalesEntityAvatar,
  SalesPageHeader,
  SalesSectionCard,
  SalesStatusChip,
  SALES,
  salesPrimaryButtonSx,
  salesTableHeadSx,
} from '../../components/sales/salesUi';

const SalesCustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({
    activity_type: 'phone',
    activity_date: new Date().toISOString().slice(0, 10),
    contact_name: '',
    notes: '',
    outcome: '',
    next_action: '',
    next_action_date: '',
    duration_minutes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [custRes, contactRes, actRes] = await Promise.all([
        salesPortalService.getCustomer(id),
        salesPortalService.getCustomerContacts(id),
        salesActivityLogService.getAll({ customer_id: id, limit: 50 }),
      ]);
      setCustomer(custRes.data);
      setContacts(contactRes.data || []);
      setActivities(actRes.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateLog = async () => {
    if (!logForm.notes?.trim()) {
      setError('Activity notes are required');
      return;
    }
    try {
      await salesActivityLogService.create({
        ...logForm,
        customer_id: parseInt(id, 10),
        duration_minutes: logForm.duration_minutes
          ? parseInt(logForm.duration_minutes, 10)
          : null,
        next_action_date: logForm.next_action_date || null,
      });
      setLogOpen(false);
      setLogForm({
        activity_type: 'phone',
        activity_date: new Date().toISOString().slice(0, 10),
        contact_name: '',
        notes: '',
        outcome: '',
        next_action: '',
        next_action_date: '',
        duration_minutes: '',
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to log activity');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (!customer) {
    return <Alert severity="error">{error || 'Customer not found'}</Alert>;
  }

  const accountRep = customer.account_rep_employee_id
    ? `${customer.account_rep_first_name || ''} ${customer.account_rep_last_name || ''}`.trim()
    : '—';
  const sourcedBy = customer.sourced_by_employee_id
    ? `${customer.sourced_by_first_name || ''} ${customer.sourced_by_last_name || ''}`.trim()
    : '—';

  return (
    <Box>
      <SalesPageHeader
        title={customer.company_name}
        subtitle={`Customer #${customer.customer_number} · Read-only account view`}
        actions={
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sales/customers')}
              sx={{ textTransform: 'none', color: SALES.muted }}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              startIcon={<RequestQuoteOutlined />}
              onClick={() => navigate(`/sales/quote-requests/new?customer_id=${id}`)}
              sx={{ textTransform: 'none', borderColor: SALES.line }}
            >
              Quote Request
            </Button>
            <Button
              variant="contained"
              startIcon={<PhoneInTalkOutlined />}
              onClick={() => setLogOpen(true)}
              sx={salesPrimaryButtonSx}
            >
              Log Call / Visit
            </Button>
          </>
        }
      />

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <SalesEntityAvatar name={customer.company_name} size={48} />
        <Box>
          <Typography sx={{ fontWeight: 700 }}>{customer.company_name}</Typography>
          <Typography variant="body2" sx={{ color: SALES.muted }}>
            {[customer.city, customer.state].filter(Boolean).join(', ') || 'No location on file'}
          </Typography>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <SalesSectionCard title="Account Details">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Phone</Typography>
            <Typography>{customer.phone || '—'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Email</Typography>
            <Typography>{customer.email || '—'}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Website</Typography>
            <Typography>{customer.website || '—'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Address</Typography>
            <Typography>
              {[customer.address_line1, customer.address_line2, [customer.city, customer.state, customer.zip_code].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Account Rep</Typography>
            <Typography>{accountRep}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Sourced By</Typography>
            <Typography>{sourcedBy}</Typography>
          </Grid>
          {customer.notes && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Notes</Typography>
              <Typography whiteSpace="pre-wrap">{customer.notes}</Typography>
            </Grid>
          )}
        </Grid>
      </SalesSectionCard>

      <SalesSectionCard title="Contacts">
        <Table size="small">
          <TableHead sx={salesTableHeadSx}>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: SALES.muted }}>No contacts on file</TableCell></TableRow>
            ) : contacts.map((c) => (
              <TableRow key={c.id}>
                <TableCell sx={{ fontWeight: 600 }}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</TableCell>
                <TableCell>{c.title || c.position || '—'}</TableCell>
                <TableCell>{c.phone || '—'}</TableCell>
                <TableCell>{c.email || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SalesSectionCard>

      <SalesSectionCard title="My Activity with this Customer" subtitle="Only your own logs are shown">
        <Table size="small">
          <TableHead sx={salesTableHeadSx}>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activities.length === 0 ? (
              <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: SALES.muted }}>No activity yet</TableCell></TableRow>
            ) : activities.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{formatDateDisplay(a.activity_date)}</TableCell>
                <TableCell>
                  <SalesStatusChip status={a.activity_type} metaMap={ACTIVITY_TYPE_META} />
                </TableCell>
                <TableCell>{a.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SalesSectionCard>

      <Dialog open={logOpen} onClose={() => setLogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Log Activity</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={logForm.activity_type}
                  onChange={(e) => setLogForm({ ...logForm, activity_type: e.target.value })}
                >
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="onsite_visit">Onsite Visit</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
                value={logForm.activity_date}
                onChange={(e) => setLogForm({ ...logForm, activity_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Contact Name"
                value={logForm.contact_name}
                onChange={(e) => setLogForm({ ...logForm, contact_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" multiline minRows={3} label="Notes" required
                value={logForm.notes}
                onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Outcome"
                value={logForm.outcome}
                onChange={(e) => setLogForm({ ...logForm, outcome: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateLog} sx={salesPrimaryButtonSx}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesCustomerDetail;
